// src/tabs/calendar/CalendarLegend.jsx — Légende repliable du calendrier
//
// Affichage PUR (aucun fetch, aucune donnée serveur) : documente les symboles
// et couleurs réellement rendus par TableauView. Replié par défaut (la grille
// est dense), se déplie au clic (toggle contrôlé par CalendarPanel : props
// `open` + `onToggle` pour rester source-de-vérité unique côté header).
//
// DA L1 (lot Gantt) — la légende reflète le NOUVEAU rendu des barres :
//   - Forme de barre UNIFORME (mêmes coins/hauteur) : seule la COULEUR encode
//     la source (Airbnb / Booking / Direct / Blocage). Plus de hachuré ni de
//     point « sous contrôle ». Le blocage garde son cadenas d'identité.
//   - Les états « calmes » (sous contrôle, paiement reçu 💰, caution reçue 🔒)
//     ne sont PLUS affichés sur les barres (redondants : une résa affichée est
//     forcément payée) → retirés de la légende.
//   - Seuls les marqueurs d'ALERTE (à traiter) restent, rhabillés en pastille
//     lucide à couleur sémantique, alignés 1:1 avec TableauView :
//       · voyageur sur place    → Flame, warning
//       · paiement à relancer   → AlertTriangle, danger
//       · caution à demander    → AlertTriangle, warning
//   - Repères de prix par cellule (inchangés) : Pin (verrou manuel) + CloudOff
//     (push non confirmé).
//
// Cohérence des valeurs (tout vient du code réel) :
//   - couleurs SOURCE résa  : _source-colors.js (SOURCE_BG + SOURCE_BORDER_STYLE)
//   - bloc gris (période bloquée) : _block-visual.js (BLOCK_BG_EMPTY)
//   - icônes/couleurs d'alerte et de prix : marqueurs de TableauView

import { Lock, AlertTriangle, Flame, Pin, CloudOff, ChevronDown, ChevronUp } from "lucide-react"
import { DS } from "../../lib/ui"
import {
  SOURCE_BG,
  SOURCE_BORDER_STYLE,
} from "./_source-colors"
import { BLOCK_BG_EMPTY } from "./_block-visual"
// v3 ÉTAPE 1 (read-path Beds24) — visuel « Bloqué sur Beds24 » (diagnostic).
import { BEDS24_ORIGIN_VISUAL } from "./_beds24-origin"
// Verrou sélectif — libellé unique du repère 📌 (texte exact validé Dimitri).
import { PRICING_LOCK_LABEL } from "../../lib/pricing-display"

// ── Fantômes calendrier (décision Dimitri 2026-07-20) ────────────────────────
// Interrupteur « Afficher les fantômes ci-dessus » : clé localStorage + parseur.
// Défaut AFFICHÉ (décision produit) → seule la valeur explicite 'false'
// masque ; null (jamais touché) / valeur corrompue → true.
export const SHOW_CANCELLED_LS_KEY = "fbz-calendar-show-cancelled"
export function parseShowCancelled(raw) {
  return raw !== "false"
}

// --- Données de légende (dérivées du code réel, pas inventées) --------------

// Sources résa : libellé + couleur de fond barre (SOURCE_BG) + style bordure
// (SOURCE_BORDER_STYLE, Booking dashed pour accessibilité daltoniens).
const SOURCES = [
  { key: "airbnb", label: "Airbnb" },
  { key: "booking", label: "Booking" },
  { key: "direct", label: "Direct" },
]

// Marqueurs d'ALERTE (à traiter) — seuls états encore rendus sur les barres.
// Couleurs sémantiques alignées 1:1 sur getAlertMarker de TableauView :
//   Flame=warning (sur place) · AlertTriangle=danger (paiement) / warning (caution).
const ALERT_ICONS = [
  { Icon: Flame, color: DS.warning, label: "Voyageur sur place" },
  { Icon: AlertTriangle, color: DS.danger, label: "Paiement à relancer" },
  { Icon: AlertTriangle, color: DS.warning, label: "Caution à demander" },
]

// Identité du blocage : cadenas marine. La couleur grise srcBloque le distingue
// déjà (famille Sources) ; le cadenas reste son marqueur d'identité sur la barre.
const BLOCK_ICON_ROW = { Icon: Lock, color: DS.marineDark, label: "Cadenas = période bloquée" }

// Repères de prix par cellule (badges coin, pas des marqueurs de barre).
// VERROU SÉLECTIF (2026-08-06) — le libellé de la punaise vient de la source
// unique PRICING_LOCK_LABEL et dit l'état RÉEL : seul le PRIX est figé ; la
// dispo et les nuits minimum continuent de suivre Beds24 sur ces dates.
const PRICE_ICONS = [
  { Icon: Pin, color: DS.marine, label: `${PRICING_LOCK_LABEL} — la dispo et les nuits minimum, elles, suivent Beds24` },
  { Icon: CloudOff, color: DS.warning, label: "Push non confirmé (désynchro Beds24 — synchro en attente)" },
]

// --- Sous-composants présentation -------------------------------------------

function FamilyTitle({ children }) {
  return (
    <div
      style={{
        fontFamily: DS.fontDisplay,
        fontSize: 14,
        fontWeight: 600,
        color: DS.marineDark,
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  )
}

// Une ligne de légende = visuel (pastille / icône) + libellé.
// Le wrapper du visuel n'est PAS aria-hidden : l'élément décoratif interne
// (ColorSwatch / icône) porte lui-même l'attribut.
function LegendRow({ visual, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: DS.textPrimary }}>
      <span style={{ width: 24, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {visual}
      </span>
      <span>{label}</span>
    </div>
  )
}

// Pastille couleur reprenant fond + style de bordure réels de la barre résa.
function ColorSwatch({ bg, borderStyle }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 22,
        height: 14,
        borderRadius: 4,
        background: bg,
        border: `1px ${borderStyle || "solid"} ${DS.marine}`,
        flexShrink: 0,
      }}
    />
  )
}

// --- Composant principal -----------------------------------------------------
//
// Découpé en deux pièces pour placement DOM distinct dans CalendarPanel :
//   - CalendarLegend        : le bouton toggle (vit dans la flex-row du header).
//   - CalendarLegend.Panel  : le panneau déplié (vit sous le header, pleine
//     largeur). CalendarPanel ne le monte que si `open`.

/**
 * Bouton toggle « Légende ▾/▴ » (contrôlé par CalendarPanel).
 * @param {boolean} open    — panneau déplié.
 * @param {function} onToggle — bascule l'état (clic bouton).
 */
export default function CalendarLegend({ open = false, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls="calendar-legend-panel"
      data-testid="calendar-legend-toggle"
      title={open ? "Masquer la légende" : "Afficher la légende"}
      className="fbz-press"
      style={{
        background: open ? DS.marine : "#eef2f7",
        color: open ? "#fff" : DS.textSecondary,
        border: "none",
        borderRadius: 9,
        padding: "7px 11px",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: DS.fontBody,
        whiteSpace: "nowrap",
        marginLeft: 6,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      Légende {open ? <ChevronUp size={14} strokeWidth={2} /> : <ChevronDown size={14} strokeWidth={2} />}
    </button>
  )
}

/**
 * Panneau déplié — 2 familles : Sources (couleur de barre) + États (marqueurs).
 *
 * Fantômes calendrier — le panneau porte aussi l'interrupteur « Afficher les
 * annulées » (case à cocher, défaut coché, persistance localStorage côté
 * CalendarPanel qui possède l'état) :
 * @param {boolean} showCancelled — fantômes affichés (défaut true).
 * @param {function} onToggleCancelled — bascule l'état (optionnel : les
 *   montages legacy sans prop rendent la légende sans l'interrupteur).
 */
function LegendPanel({ showCancelled = true, onToggleCancelled = null }) {
  return (
    <div
      id="calendar-legend-panel"
      data-testid="calendar-legend-panel"
      role="region"
      aria-label="Légende du calendrier"
      style={{
        marginBottom: 14,
        background: DS.white,
        border: `0.5px solid ${DS.border}`,
        borderRadius: 12,
        padding: "16px 18px",
        boxShadow: "0 2px 8px rgba(0,0,0,.06)",
        display: "flex",
        gap: 40,
        flexWrap: "wrap",
        fontFamily: DS.fontBody,
      }}
    >
      {/* Famille 1 — Sources (couleur de fond de barre). Forme uniforme :
          seule la couleur distingue la source (DA L1 #5). */}
      <div style={{ minWidth: 240 }}>
        <FamilyTitle>Sources</FamilyTitle>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {SOURCES.map((s) => (
            <LegendRow
              key={s.key}
              visual={<ColorSwatch bg={SOURCE_BG[s.key]} borderStyle={SOURCE_BORDER_STYLE[s.key]} />}
              label={s.label}
            />
          ))}
          <LegendRow
            visual={<ColorSwatch bg={BLOCK_BG_EMPTY} borderStyle="solid" />}
            label="Période bloquée (travaux, indispo)"
          />
          {/* v3 ÉTAPE 1 (read-path Beds24) — diagnostic TEMPORAIRE : trame
              hachurée = blocage présent côté Beds24 mais non représenté dans
              l'app (à rapprocher). */}
          <LegendRow
            visual={<ColorSwatch bg={BEDS24_ORIGIN_VISUAL.background} borderStyle="dashed" />}
            label="Bloqué sur Beds24 (non représenté dans l'app)"
          />
          {/* Fantômes calendrier — pastille alignée sur le rendu réel de la
              barre fantôme TableauView : transparent, pointillé gris, opacité
              réduite. Les dates restent vendables (barre non bloquante). */}
          <LegendRow
            visual={
              <span
                aria-hidden="true"
                style={{
                  display: "inline-block",
                  width: 22,
                  height: 12,
                  borderRadius: 4,
                  background: "transparent",
                  border: "1.5px dashed #9ca3af",
                  opacity: 0.7,
                  flexShrink: 0,
                }}
              />
            }
            label="Résa directe annulée (fantôme — dates re-vendables)"
          />
          {/* Interrupteur (défaut coché, persistance localStorage côté
              CalendarPanel). Décoché → aucun fantôme rendu (filtre 100 %
              client, le payload continue de les servir).
              PÉRIMÈTRE EXACT — cet interrupteur ne pilote QUE les fantômes
              ci-dessus, c'est-à-dire les demandes directes annulées
              (pricing_blocks 'cancelled'). Les RÉSERVATIONS annulées
              (reservations.status='cancelled', OTA ou directes) sont retirées
              du Tableau inconditionnellement et ne sont consultables que dans
              la vue Liste — l'ancien libellé « Afficher les annulées »
              laissait croire l'inverse. */}
          {typeof onToggleCancelled === "function" && (
            <label
              data-testid="calendar-legend-show-cancelled"
              title="Ne concerne que les fantômes ci-dessus (demandes directes annulées). Les réservations annulées ne sont jamais dessinées dans le Tableau : elles sont consultables dans la vue Liste."
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12.5,
                color: DS.textPrimary,
                cursor: "pointer",
                marginTop: 2,
              }}
            >
              <span style={{ width: 24, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <input
                  type="checkbox"
                  checked={showCancelled}
                  onChange={onToggleCancelled}
                  data-testid="calendar-legend-show-cancelled-checkbox"
                  style={{ cursor: "pointer", margin: 0 }}
                />
              </span>
              <span>Afficher les fantômes ci-dessus</span>
            </label>
          )}
        </div>
        <div style={{ fontSize: 11.5, color: DS.textMuted, marginTop: 10, lineHeight: 1.45 }}>
          Barres de forme uniforme : seule la couleur distingue la source. Le
          nom du voyageur reste toujours visible et prioritaire. La trame
          hachurée signale une période bloquée côté Beds24 encore à rapprocher
          dans l'app.
        </div>
      </div>

      {/* Famille 2 — États (marqueurs superposés à la barre).
          DA L1 #3/#4 : uniquement les ALERTES à traiter, puis l'identité du
          blocage et les repères de prix par cellule. Les états calmes (payé /
          caution reçue / sous contrôle) ne sont plus rendus → absents ici. */}
      <div style={{ minWidth: 240 }}>
        <FamilyTitle>États</FamilyTitle>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {ALERT_ICONS.map((it) => (
            <LegendRow
              key={it.label}
              visual={<it.Icon size={15} strokeWidth={2.2} color={it.color} />}
              label={it.label}
            />
          ))}
          <div style={{ height: 4 }} />
          <LegendRow
            visual={<BLOCK_ICON_ROW.Icon size={15} strokeWidth={2} color={BLOCK_ICON_ROW.color} />}
            label={BLOCK_ICON_ROW.label}
          />
          <div style={{ height: 4 }} />
          {PRICE_ICONS.map((it) => (
            <LegendRow
              key={it.label}
              visual={<it.Icon size={15} strokeWidth={2} color={it.color} />}
              label={it.label}
            />
          ))}
          <LegendRow visual={null} label="Auto : aucune punaise — le prix suit Beds24" />
        </div>
      </div>
    </div>
  )
}

CalendarLegend.Panel = LegendPanel
